import { useState, type FormEvent, type ReactNode } from 'react';
import { useClerk, useSignUp } from '@clerk/clerk-react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  AuthCard,
  FormField,
  PasswordInput,
  VerificationCodeInput,
  SubmitButton,
  Button,
} from '@legacyorb/ui';
import { Input } from '@/components/ui/input';

type View = 'name' | 'credentials' | 'verify';

interface SignUpPageProps {
  /**
   * Where to land after the account is created (and e-mail verified).
   * Defaults to the standard onboarding flow; the purchase funnel passes
   * '/start/account' so the user returns to complete the intent step
   * (design §4.1, review F8).
   */
  redirectTo?: string;
  /**
   * 'default' — the standalone two-step signup page with its own chrome
   * (logo header + 2-dot indicator).
   * 'funnel'  — a single combined card (name + credentials + any
   * renderExtraFields content) with no page chrome; the caller wraps the
   * result in FunnelLayout. Both variants share one Clerk engine
   * (signUp.create + e-mail verification) so the flow is never duplicated
   * (review F8).
   */
  variant?: 'default' | 'funnel';
  /** Funnel mode: extra fields rendered between the password and the CTA. */
  renderExtraFields?: (ctx: { isSubmitting: boolean }) => ReactNode;
  /**
   * Funnel mode: runs right before signUp.create — return false to block the
   * submit (e.g. failed intent validation). Use it to persist the intent
   * selections before the Clerk verification round-trip takes over.
   */
  onBeforeSubmit?: () => boolean;
  /** Funnel mode: extra disable condition for the CTA. */
  submitDisabled?: boolean;
}

export function SignUpPage({
  redirectTo = '/welcome/terms',
  variant = 'default',
  renderExtraFields,
  onBeforeSubmit,
  submitDisabled = false,
}: SignUpPageProps = {}) {
  const isFunnel = variant === 'funnel';
  const { signUp, setActive, isLoaded } = useSignUp();
  const { signOut } = useClerk();
  const navigate = useNavigate();

  // The funnel variant has no separate "name" step — everything lives on the
  // combined credentials card.
  const [view, setView] = useState<View>(isFunnel ? 'credentials' : 'name');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNextFromName = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!firstName.trim()) {
      setError('Please enter your first name.');
      return;
    }
    setView('credentials');
  };

  const handleSignUp = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;

    setError('');

    if (isFunnel) {
      // The combined card has no prior "name" step, so validate it here.
      if (!firstName.trim()) {
        setError('Please enter your first name.');
        return;
      }
      if (onBeforeSubmit && !onBeforeSubmit()) return;
    }

    setIsSubmitting(true);

    try {
      const result = await signUp.create({
        firstName,
        lastName,
        emailAddress: email,
        password,
      });

      if (result.status === 'complete' && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        navigate(redirectTo);
        return;
      }

      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setView('verify');
    } catch (err: unknown) {
      const clerkError = err as { errors?: Array<{ code?: string; message: string }> };
      const errorCode = clerkError.errors?.[0]?.code;
      if (errorCode === 'session_exists') {
        await signOut();
        navigate('/sign-in');
        return;
      }
      const message = clerkError.errors?.[0]?.message || 'Failed to sign up. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;

    setError('');
    setIsSubmitting(true);

    try {
      const result = await signUp.attemptEmailAddressVerification({ code });

      if (result.status === 'complete' && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        navigate(redirectTo);
      } else {
        setError('Verification could not be completed. Please try again.');
      }
    } catch (err: unknown) {
      const clerkError = err as { errors?: Array<{ message: string }> };
      const message = clerkError.errors?.[0]?.message || 'Invalid verification code.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSteps = 2;
  const currentStep = view === 'name' ? 1 : view === 'credentials' ? 2 : 2;

  // Shared by both variants — the unavoidable Clerk e-mail verification step.
  // In funnel mode the caller's FunnelLayout (progress bar) wraps it.
  const verifyCard = (
    <AuthCard title="Verify Email" description={`Enter the 6-digit code sent to ${email}`}>
      <form onSubmit={handleVerify} className="space-y-6">
        <FormField label="Verification Code" htmlFor="code">
          <VerificationCodeInput
            id="code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            disabled={isSubmitting}
          />
        </FormField>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <SubmitButton
          variant="brand"
          size="lg"
          isSubmitting={isSubmitting}
          loadingText="Verifying..."
          disabled={code.length !== 6}
        >
          Verify
        </SubmitButton>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => {
            setView('credentials');
            setError('');
            setCode('');
          }}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </form>
    </AuthCard>
  );

  if (isFunnel) {
    if (view === 'verify') return verifyCard;

    // Funnel step 1 — one combined card: name + credentials + the caller's
    // intent fields (who-for, preferred package, consent), single CTA.
    return (
      <AuthCard
        title="Create your account"
        description="A few details so we can set up your Legacy Orb experience."
      >
        <form onSubmit={handleSignUp} className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="First Name" htmlFor="firstName">
              <Input
                id="firstName"
                type="text"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
                autoFocus
                disabled={isSubmitting}
              />
            </FormField>
            <FormField label="Last Name" htmlFor="lastName">
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                disabled={isSubmitting}
              />
            </FormField>
          </div>

          <FormField label="Email" htmlFor="email">
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={isSubmitting}
            />
          </FormField>

          <FormField label="Password" htmlFor="password">
            <PasswordInput
              id="password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              disabled={isSubmitting}
            />
          </FormField>

          {renderExtraFields?.({ isSubmitting })}

          {error && <p className="text-sm text-destructive">{error}</p>}

          {/* Required mount for Clerk smart CAPTCHA — do not remove. */}
          <div id="clerk-captcha" />

          <SubmitButton
            variant="brand"
            size="lg"
            isSubmitting={isSubmitting}
            loadingText="Creating account..."
            disabled={!isLoaded || !firstName.trim() || !email || !password || submitDisabled}
          >
            Create account & continue
          </SubmitButton>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/sign-in" className="text-foreground underline-offset-4 hover:underline">
              Sign In
            </Link>
          </p>
        </form>
      </AuthCard>
    );
  }

  return (
    <div className="auth-theme flex min-h-screen items-center justify-center bg-background px-4 py-8 sm:py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center sm:mb-8">
          <Link to="/" className="mb-4 inline-flex items-center gap-2">
            <img src="/logos/orb-mark.png" alt="LegacyOrb" className="h-10 w-10 sm:h-12 sm:w-12" />
            <span className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              LegacyOrb
            </span>
          </Link>
          <h1 className="font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Welcome
          </h1>
          <p className="mt-2 text-sm text-muted-foreground sm:text-base">
            Create your account to get started
          </p>
        </div>

        {view !== 'verify' && (
          <div className="mb-4 flex items-center justify-center gap-2" aria-hidden="true">
            {Array.from({ length: totalSteps }).map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 w-8 rounded-full transition-colors ${
                  idx < currentStep ? 'bg-primary' : 'bg-border'
                }`}
              />
            ))}
          </div>
        )}

        {view === 'verify' ? (
          verifyCard
        ) : view === 'name' ? (
          <AuthCard title="What's your name?" description={`Step 1 of ${totalSteps}`}>
            <form onSubmit={handleNextFromName} className="space-y-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField label="First Name" htmlFor="firstName">
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    autoComplete="given-name"
                    autoFocus
                  />
                </FormField>
                <FormField label="Last Name" htmlFor="lastName">
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                  />
                </FormField>
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <SubmitButton
                variant="brand"
                size="lg"
                isSubmitting={false}
                disabled={!firstName.trim()}
              >
                Continue
              </SubmitButton>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/sign-in" className="text-foreground underline-offset-4 hover:underline">
                  Sign In
                </Link>
              </p>
            </form>
          </AuthCard>
        ) : (
          <AuthCard
            title="Create your login"
            description={`Step 2 of ${totalSteps} — Hi ${firstName}, almost there.`}
          >
            <form onSubmit={handleSignUp} className="space-y-6">
              <FormField label="Email" htmlFor="email">
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  disabled={isSubmitting}
                  autoFocus
                />
              </FormField>

              <FormField label="Password" htmlFor="password">
                <PasswordInput
                  id="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  disabled={isSubmitting}
                />
              </FormField>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <div id="clerk-captcha" />

              <SubmitButton
                variant="brand"
                size="lg"
                isSubmitting={isSubmitting}
                loadingText="Creating account..."
                disabled={!isLoaded || !email || !password}
              >
                Create Account
              </SubmitButton>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  setView('name');
                  setError('');
                }}
                disabled={isSubmitting}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/sign-in" className="text-foreground underline-offset-4 hover:underline">
                  Sign In
                </Link>
              </p>
            </form>
          </AuthCard>
        )}
      </div>
    </div>
  );
}
